import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Column, Pie } from '@ant-design/charts';
import { Avatar, Button, Pagination, Progress, Typography } from 'antd';
import { useIntl } from 'react-intl';

import { displayProgressPercentage } from '@src/packages/utils/functions/triggers/progress';

import { ResponsiveTable, type ResponsiveTableColumn } from '@src/components/Table';
import DrilldownDrawer, {
  DRILLDOWN_TABLE_MAX_HEIGHT,
} from '@src/packages/dashboard/components/ReportSummary/DrilldownDrawer';
import ChartControls, {
  type ChartViewLabels,
} from '@src/packages/dashboard/components/ReportSummary/ChartControls';
import PercentTrendChart from '@src/packages/dashboard/components/ReportSummary/PercentTrendChart';
import type { ChartView } from '@src/packages/dashboard/components/ReportSummary/types';
import ReportGrid from '@src/packages/dashboard/components/ReportSummary/ReportGrid';
import ReportSection from '@src/packages/dashboard/components/ReportSummary/ReportSection';
import type { SectionStatus } from '@src/packages/dashboard/components/ReportSummary/SectionStatus';
import MetricsPanel from '@src/packages/dashboard/components/ReportSummary/MetricCards';
import { getHistorySectionErrorMessageId } from '@src/packages/dashboard/components/ReportSummary/historySectionErrorMessageIds';
import type {
  ExecutionsHistoryErrorCode,
  ExecutionsHistoryScheduleQuery,
  ExecutionsHistoryScheduleSection,
  ScheduleDrilldownTarget,
  ScheduleDrilldownSectionData,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';

import type { ScheduleChartFilters } from '../../schedulesFilterConfig';
import {
  type ScheduleSummaryComplianceRange,
  type ScheduleSummaryEventComplianceRange,
  type ScheduleSummaryEventType,
  type ScheduleSummaryProgressItem,
  type ScheduleSummarySchedule,
  type ScheduleSummaryData,
  type ScheduleSummaryUser,
  type ScheduleSummaryUserRange,
} from './types';
import styles from './styles.module.less';
import {
  formatPeriodLabel,
  getChartPageSize,
  getComplianceRangeColor,
  getComplianceColor,
  getSchedulePeriodCompletion,
  getScheduleExecutionCounts,
  getDivisionComplianceColor,
  getScheduleDrilldownTarget,
  SCHEDULE_DIVISION_COLORS,
  SCHEDULE_EXECUTION_COLORS,
  SCHEDULE_RESULT_COLORS,
  shouldShowDivisionCompliance,
} from './utils';
import { useScheduleHistorySections } from './hooks/useScheduleHistorySections';
import type { ScheduleHistorySectionsController } from './hooks/useScheduleHistorySections';
import { buildScheduleSummaryData, buildScheduleUserMetricValues } from './historyData';
import { useScheduleHistoryDrilldown } from './hooks/useScheduleHistoryDrilldown';
import SectionStatusContent from '@src/packages/dashboard/components/ReportSummary/SectionStatus';

const { Text } = Typography;

const DIVISION_PAGE_SIZE = 5;
const USER_METRIC_CARD_KEYS = [
  'assigned',
  'active',
  'complete',
  'partial',
  'inactive',
  'average',
] as const;

const normalizeSearchValue = (value: string) =>
  value.trim().toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const getUserInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toLocaleUpperCase())
    .join('');

interface ScheduleSummaryProps {
  filters: ScheduleChartFilters;
  divisionIds?: string[];
  variant?: 'schedules' | 'routes';
  historyQuery?: ExecutionsHistoryScheduleQuery;
  history?: ScheduleHistorySectionsController;
  dataOverride?: ScheduleSummaryData;
  sectionStatuses?: Partial<Record<ExecutionsHistoryScheduleSection, SectionStatus>>;
  externalDrilldown?: {
    data?: ScheduleDrilldownSectionData;
    error: boolean;
    errorCode?: ExecutionsHistoryErrorCode;
    loading: boolean;
    pageSize: number;
    retry: () => void;
  };
  onDrilldownRequest?: (request: {
    target: ScheduleDrilldownTarget | null;
    search: string;
    page: number;
  }) => void;
  complianceChartView?: ChartView;
  onComplianceChartViewChange?: (view: ChartView) => void;
}

interface SummarySectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
  headerExtra?: React.ReactNode;
  context?: React.ReactNode;
  footer?: React.ReactNode;
  status?: SectionStatus;
}

const SummarySection: React.FC<SummarySectionProps> = ({
  title,
  description,
  children,
  className,
  headerExtra,
  context,
  footer,
  status,
}) => (
  <ReportSection title={title} description={description} className={`${styles.section} ${className ?? ''}`} headerExtra={headerExtra} status={status}>
    {context}
    {children}
    {footer}
  </ReportSection>
);

const ProgressList: React.FC<{
  items: ScheduleSummaryProgressItem[];
  separated?: boolean;
  showRank?: boolean;
  onValueClick?: (item: ScheduleSummaryProgressItem) => void;
  className?: string;
  testId?: string;
}> = ({ items, separated = false, showRank = false, onValueClick, className, testId }) => {
  const maxValue = Math.max(...items.map(item => item.value), 1);

  return (
    <div
      className={`${styles.progressList} ${separated ? styles.separatedProgressList : ''} ${className ?? ''}`}
      data-testid={testId}
    >
      {items.map((item, index) => {
        const isPercentage = item.isPercentage || Boolean(item.detail);
        const percent = isPercentage
          ? item.value
          : Math.round((item.value / maxValue) * 100);
        return (
          <div key={item.key} className={styles.progressRow}>
            {showRank && (
              <Text className={styles.progressRank} type="secondary">
                #{index + 1}
              </Text>
            )}
            {item.color && (
              <span
                className={styles.progressColorIndicator}
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              />
            )}
            <Text ellipsis={{ tooltip: item.label }}>{item.label}</Text>
            <Progress
              className={styles[`${item.tone ?? 'primary'}Progress`]}
              percent={percent}
              strokeColor={item.barColor}
              showInfo={false}
              strokeWidth={6}
            />
            <span className={styles.progressValue}>
              {onValueClick ? (
                <Button
                  aria-label={item.actionLabel}
                  className={styles.progressValueButton}
                  type="link"
                  onClick={() => onValueClick(item)}
                >
                  {isPercentage ? `${item.value}%` : item.value}
                </Button>
              ) : (
                <Text>{isPercentage ? `${item.value}%` : item.value}</Text>
              )}
              {item.detail && <Text type="secondary"> · {item.detail}</Text>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ScheduleSummary: React.FC<ScheduleSummaryProps> = ({
  filters,
  divisionIds = [],
  variant = 'schedules',
  historyQuery,
  history: historySource,
  dataOverride,
  sectionStatuses,
  externalDrilldown,
  onDrilldownRequest,
  complianceChartView: controlledComplianceChartView,
  onComplianceChartViewChange,
}) => {
  const baseIntl = useIntl();
  const intl = useMemo(() => {
    if (variant !== 'routes') return baseIntl;

    return {
      ...baseIntl,
      formatMessage: ((descriptor, values) => baseIntl.formatMessage({
        ...descriptor,
        id: descriptor.id?.replace('dashboards.schedules.summary.', 'dashboards.routes.summary.'),
      }, values)) as typeof baseIntl.formatMessage,
    };
  }, [baseIntl, variant]);
  const [compliancePage, setCompliancePage] = useState(0);
  const [localComplianceChartView, setLocalComplianceChartView] = useState<ChartView>('bar');
  const complianceChartView = controlledComplianceChartView ?? localComplianceChartView;
  const handleComplianceChartViewChange = (view: ChartView) => {
    setLocalComplianceChartView(view);
    onComplianceChartViewChange?.(view);
  };
  const [executionPage, setExecutionPage] = useState(0);
  const [divisionAllocationPage, setDivisionAllocationPage] = useState(0);
  const [divisionCompliancePage, setDivisionCompliancePage] = useState(0);
  const [selectedScheduleRange, setSelectedScheduleRange] =
    useState<ScheduleSummaryComplianceRange | null>(null);
  const [selectedEventRange, setSelectedEventRange] =
    useState<ScheduleSummaryEventComplianceRange | null>(null);
  const [selectedUserRange, setSelectedUserRange] = useState<ScheduleSummaryUserRange | null>(null);
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [drilldownPage, setDrilldownPage] = useState(1);
  const [chartWidth, setChartWidth] = useState(0);
  const complianceChartRef = useRef<HTMLDivElement>(null);
  const executionChartRef = useRef<HTMLDivElement>(null);
  const localHistory = useScheduleHistorySections(historySource ? undefined : historyQuery);
  const history = historySource ?? localHistory;
  const apiData = useMemo(() => buildScheduleSummaryData(history.sections), [history.sections]);
  const data = dataOverride ?? apiData;
  const scheduleDrilldownTarget = useMemo<ScheduleDrilldownTarget | null>(() => {
    if (selectedScheduleRange) {
      if (selectedScheduleRange.drilldownFilter) {
        return { kind: 'schedules', filter: selectedScheduleRange.drilldownFilter };
      }
      return getScheduleDrilldownTarget('schedules', selectedScheduleRange.key);
    }
    if (selectedEventRange) {
      if (selectedEventRange.drilldownFilter) {
        return { kind: 'eventTypes', filter: selectedEventRange.drilldownFilter };
      }
      return getScheduleDrilldownTarget('eventTypes', selectedEventRange.key);
    }
    if (selectedUserRange) {
      if (selectedUserRange.drilldownFilter) {
        return { kind: 'users', filter: selectedUserRange.drilldownFilter };
      }
      return getScheduleDrilldownTarget('users', selectedUserRange.key);
    }
    return null;
  }, [selectedEventRange, selectedScheduleRange, selectedUserRange]);
  const activeDrilldownSearch = selectedScheduleRange
    ? scheduleSearch
    : selectedEventRange
      ? eventSearch
      : userSearch;
  const scheduleDrilldown = useScheduleHistoryDrilldown(
    historyQuery,
    scheduleDrilldownTarget,
    activeDrilldownSearch,
    drilldownPage,
  );
  useEffect(() => {
    if (variant !== 'routes' || !onDrilldownRequest) return;
    onDrilldownRequest({
      target: scheduleDrilldownTarget,
      search: activeDrilldownSearch,
      page: drilldownPage,
    });
  }, [activeDrilldownSearch, drilldownPage, onDrilldownRequest, scheduleDrilldownTarget, variant]);
  useEffect(() => {
    setDrilldownPage(1);
  }, [scheduleDrilldownTarget]);

  useEffect(() => {
    setDrilldownPage(1);
  }, [activeDrilldownSearch]);
  const statusFor = (section: ExecutionsHistoryScheduleSection): SectionStatus => {
    if (sectionStatuses?.[section]) return sectionStatuses[section];
    const state = history.sections[section];
    if (!historyQuery && !historySource) return { kind: 'ready' };
    if (state.loading || !state.result) return { kind: 'loading' };
    if (state.result.status === 'error') {
      return {
        kind: 'error',
        message: intl.formatMessage({ id: getHistorySectionErrorMessageId(state.result.errorCode) }),
        retryLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.section.retry' }),
        onRetry: () => history.retrySection(section),
      };
    }
    return { kind: 'ready' };
  };
  const apiUserMetricValues = buildScheduleUserMetricValues(data);
  const chartPageSize = getChartPageSize(
    chartWidth,
    filters.groupBy,
  );
  const compliancePageCount = Math.ceil(data.period.length / chartPageSize);
  const currentCompliancePage = Math.min(compliancePage, Math.max(compliancePageCount - 1, 0));
  const compliancePagePoints = data.period.slice(
    currentCompliancePage * chartPageSize,
    (currentCompliancePage + 1) * chartPageSize,
  );
  const executionPageCount = Math.ceil(data.period.length / chartPageSize);
  const currentExecutionPage = Math.min(executionPage, Math.max(executionPageCount - 1, 0));
  const executionPagePoints = data.period.slice(
    currentExecutionPage * chartPageSize,
    (currentExecutionPage + 1) * chartPageSize,
  );
  const showDivisionCompliance = shouldShowDivisionCompliance(divisionIds);
  const allocationPageCount = Math.ceil(data.divisionScheduleAssignments.length / DIVISION_PAGE_SIZE);
  const currentAllocationPage = Math.min(divisionAllocationPage, Math.max(allocationPageCount - 1, 0));
  const allocationPageItems = data.divisionScheduleAssignments.slice(
    currentAllocationPage * DIVISION_PAGE_SIZE,
    (currentAllocationPage + 1) * DIVISION_PAGE_SIZE,
  );
  const allocationPageTotal = allocationPageItems.reduce((total, item) => total + item.value, 0);
  const divisionCompletionItems = data.divisionCompliance.map(item => {
    const color = getDivisionComplianceColor(item.value);

    return { ...item, color, barColor: color, isPercentage: true };
  });
  const divisionCompliancePageCount = Math.ceil(divisionCompletionItems.length / DIVISION_PAGE_SIZE);
  const currentDivisionCompliancePage = Math.min(
    divisionCompliancePage,
    Math.max(divisionCompliancePageCount - 1, 0),
  );
  const divisionCompliancePageItems = divisionCompletionItems.slice(
    currentDivisionCompliancePage * DIVISION_PAGE_SIZE,
    (currentDivisionCompliancePage + 1) * DIVISION_PAGE_SIZE,
  );
  const scheduleDrawerColumns: ResponsiveTableColumn<ScheduleSummarySchedule>[] = [
    {
      key: 'schedule-name',
      title: intl.formatMessage({ id: 'dashboards.schedules.summary.scheduleCompliance.name' }),
      dataIndex: 'name',
      render: schedule => <Text>{schedule.name}</Text>,
    },
    {
      key: 'schedule-division',
      title: intl.formatMessage({ id: 'schedules.list.division.label' }),
      dataIndex: 'divisionName',
      width: 180,
      ellipsis: true,
      render: (schedule: ScheduleSummarySchedule) => (
        <Text ellipsis={{ tooltip: schedule.divisionName || '-' }}>
          {schedule.divisionName || '-'}
        </Text>
      ),
    },
    {
      key: 'schedule-compliance',
      dataIndex: 'completion',
      align: 'right',
      width: 140,
      title: (
        <span className={styles.scheduleDrawerNoWrap}>
          {intl.formatMessage({ id: 'dashboards.schedules.summary.scheduleCompliance.rate' })}
        </span>
      ),
      render: schedule => <Text strong>{schedule.completion}%</Text>,
    },
    {
      key: 'schedule-executions',
      title: intl.formatMessage({ id: 'dashboards.schedules.summary.scheduleCompliance.completed' }),
      dataIndex: 'completed',
      align: 'right',
      width: 160,
      render: schedule => `${schedule.completed} de ${schedule.total}`,
    },
  ];
  const eventDrawerColumns: ResponsiveTableColumn<ScheduleSummaryEventType>[] = [
    {
      key: 'event-type-name',
      title: intl.formatMessage({ id: 'dashboards.schedules.summary.eventCompliance.name' }),
      dataIndex: 'label',
    },
    {
      key: 'event-type-compliance',
      dataIndex: 'completion',
      align: 'right',
      width: 140,
      title: (
        <span className={styles.scheduleDrawerNoWrap}>
          {intl.formatMessage({ id: 'dashboards.schedules.summary.eventCompliance.rate' })}
        </span>
      ),
      render: eventType => <Text strong>{eventType.completion}%</Text>,
    },
    {
      key: 'event-type-executions',
      title: intl.formatMessage({ id: 'dashboards.schedules.summary.eventCompliance.executions' }),
      dataIndex: 'completed',
      align: 'right',
      width: 180,
      render: eventType => `${eventType.completed} de ${eventType.total}`,
    },
  ];
  const userMessages = variant === 'routes'
    ? 'dashboards.routes.summary.users'
    : 'dashboards.schedules.summary.users';
  const userDrawerColumns: ResponsiveTableColumn<ScheduleSummaryUser>[] = [
    {
      key: 'user-name',
      title: intl.formatMessage({ id: `${userMessages}.name` }),
      dataIndex: 'name',
      render: (user) => (
        <div className={styles.userDrawerIdentity}>
          <Avatar className={styles.userInitials} size={32}>
            {getUserInitials(user.name)}
          </Avatar>
          <Text>{user.name}</Text>
        </div>
      ),
    },
    {
      key: 'user-rut',
      title: intl.formatMessage({ id: `${userMessages}.rut` }),
      dataIndex: 'identifier',
      width: 180,
    },
    {
      key: 'user-compliance',
      title: (
        <span className={styles.scheduleDrawerNoWrap}>
          {intl.formatMessage({ id: `${userMessages}.rate` })}
        </span>
      ),
      dataIndex: 'completion',
      align: 'right',
      width: 140,
      render: (user) => <Text strong>{user.completion}%</Text>,
    },
    {
      key: 'user-schedules',
      title: intl.formatMessage({ id: `${userMessages}.completed` }),
      dataIndex: 'completed',
      align: 'right',
      width: 180,
      render: (user) => `${user.completed} de ${user.total}`,
    },
  ];

  useEffect(() => {
    setCompliancePage(0);
    setExecutionPage(0);
  }, [filters]);

  useEffect(() => {
    setCompliancePage(0);
    setExecutionPage(0);
  }, [chartPageSize]);

  useEffect(() => {
    const chartElement = complianceChartRef.current;
    if (!chartElement) {
      return undefined;
    }

    const executionElement = executionChartRef.current;
    const updateWidth = () => {
      const nextWidth = Math.min(
        chartElement.clientWidth,
        executionElement?.clientWidth || chartElement.clientWidth,
      );
      setChartWidth(nextWidth);
    };
    updateWidth();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateWidth);
    observer.observe(chartElement);
    if (executionElement) observer.observe(executionElement);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setScheduleSearch('');
  }, [selectedScheduleRange?.key]);

  useEffect(() => {
    setEventSearch('');
  }, [selectedEventRange?.key]);

  useEffect(() => {
    setUserSearch('');
  }, [selectedUserRange?.key]);

  const filteredDrawerSchedules = useMemo(() => {
    const normalizedSearch = normalizeSearchValue(scheduleSearch);

    if (!selectedScheduleRange || !normalizedSearch) {
      return selectedScheduleRange?.schedules ?? [];
    }

    return selectedScheduleRange.schedules.filter(schedule =>
      normalizeSearchValue(`${schedule.name} ${schedule.divisionName ?? ''}`).includes(
        normalizedSearch,
      ),
    );
  }, [scheduleSearch, selectedScheduleRange]);

  const filteredDrawerEventTypes = useMemo(() => {
    const normalizedSearch = normalizeSearchValue(eventSearch);

    if (!selectedEventRange || !normalizedSearch) {
      return selectedEventRange?.eventTypes ?? [];
    }

    return selectedEventRange.eventTypes.filter(eventType =>
      normalizeSearchValue(eventType.label).includes(normalizedSearch),
    );
  }, [eventSearch, selectedEventRange]);

  const filteredDrawerUsers = useMemo(() => {
    const normalizedSearch = normalizeSearchValue(userSearch);

    if (!selectedUserRange || !normalizedSearch) {
      return selectedUserRange?.users ?? [];
    }

    return selectedUserRange.users.filter(user =>
      normalizeSearchValue(`${user.name} ${user.identifier}`).includes(normalizedSearch),
    );
  }, [selectedUserRange, userSearch]);

  const activeDrilldown = externalDrilldown ?? scheduleDrilldown;
  const hasScheduleApi = Boolean(historyQuery || historySource || externalDrilldown);
  const drilldownErrorCode = externalDrilldown?.errorCode ?? (
    scheduleDrilldown.result?.status === 'error' ? scheduleDrilldown.result.errorCode : undefined
  );
  const drilldownFailed = externalDrilldown
    ? externalDrilldown.error
    : scheduleDrilldown.result?.status === 'error';
  const drilldownStatus: SectionStatus = drilldownFailed
      ? {
        kind: 'error',
        message: intl.formatMessage({
          id: getHistorySectionErrorMessageId(drilldownErrorCode),
        }),
        retryLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.section.retry' }),
        onRetry: activeDrilldown.retry,
      }
      : { kind: 'ready' };
  const drawerSchedules = hasScheduleApi && activeDrilldown.data?.kind === 'schedules'
    ? activeDrilldown.data.rows
    : filteredDrawerSchedules;
  const drawerEventTypes = hasScheduleApi && activeDrilldown.data?.kind === 'eventTypes'
    ? activeDrilldown.data.rows
    : filteredDrawerEventTypes;
  const drawerUsers: ScheduleSummaryUser[] = hasScheduleApi && activeDrilldown.data?.kind === 'users'
    ? activeDrilldown.data.rows
    : filteredDrawerUsers;
  const drawerTotal = hasScheduleApi
    ? activeDrilldown.data?.total
      ?? selectedScheduleRange?.value
      ?? selectedEventRange?.value
      ?? selectedUserRange?.value
      ?? 0
    : selectedScheduleRange?.schedules.length ?? selectedEventRange?.eventTypes.length ?? selectedUserRange?.users.length ?? 0;
  const drawerPagination = hasScheduleApi
    ? {
      current: drilldownPage,
      disabled: activeDrilldown.loading,
      pageSize: activeDrilldown.pageSize,
      total: drawerTotal,
      onChange: setDrilldownPage,
    }
    : undefined;

  const resultChartData = useMemo(
    () =>
      data.results.map(result => ({
        ...result,
        status: intl.formatMessage({ id: `dashboards.schedules.summary.result.${result.key}` }),
      })),
    [data.results, intl],
  );

  const complianceData = useMemo(
    () =>
      compliancePagePoints.map(point => ({
        period: formatPeriodLabel(point.period, filters.groupBy, intl),
        value: getSchedulePeriodCompletion(point),
        completed: point.completed,
        total: point.expected,
        color: getComplianceColor(getSchedulePeriodCompletion(point)),
      })),
    [filters.groupBy, intl, compliancePagePoints],
  );
  const chartViewLabels: ChartViewLabels = {
    ariaLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.chartView' }),
    bar: intl.formatMessage({ id: 'dashboards.tasks.summary.chartView.bar' }),
    line: intl.formatMessage({ id: 'dashboards.tasks.summary.chartView.line' }),
  };

  const executionsData = useMemo(
    () =>
      executionPagePoints.flatMap(point => {
        const period = formatPeriodLabel(point.period, filters.groupBy, intl);
        return [
          {
            period,
            type: intl.formatMessage({ id: variant === 'routes' ? 'dashboards.routes.summary.executions.required' : 'dashboards.schedules.summary.executions.required' }),
            value: getScheduleExecutionCounts(point, variant).expected,
          },
          {
            period,
            type: intl.formatMessage({ id: variant === 'routes' ? 'dashboards.routes.summary.executions.completed' : 'dashboards.schedules.summary.executions.completed' }),
            value: getScheduleExecutionCounts(point, variant).completed,
          },
        ];
      }),
    [filters.groupBy, intl, executionPagePoints, variant],
  );

  const executionCounts = variant === 'schedules'
    ? data.period.reduce((totals, point) => ({ expected: totals.expected + point.expected,
      completed: totals.completed + point.completed }), { expected: 0, completed: 0 })
    : { expected: data.executionSummary.total, completed: data.executionSummary.completed };
  const executionCompletion = executionCounts.expected > 0
    ? displayProgressPercentage(executionCounts.completed / executionCounts.expected * 100) : 0;

  const metricLabels = {
    schedules: intl.formatMessage({ id: 'dashboards.schedules.summary.metric.schedules' }),
    completed: intl.formatMessage({ id: 'dashboards.schedules.summary.metric.completed' }),
    partial: intl.formatMessage({ id: 'dashboards.schedules.summary.metric.partial' }),
    inactive: intl.formatMessage({ id: 'dashboards.schedules.summary.metric.inactive' }),
    compliance: intl.formatMessage({ id: 'dashboards.schedules.summary.metric.compliance' }),
    averageTime: intl.formatMessage({ id: 'dashboards.schedules.summary.metric.averageTime' }),
  };
  const openScheduleMetric = (key: 'schedules' | 'completed' | 'partial' | 'inactive') => {
    const allSchedules = data.scheduleCompletionRanges.flatMap(range => range.schedules);
    const schedules = allSchedules.filter(schedule => {
      if (key === 'completed') return schedule.completion === 100;
      if (key === 'partial') return schedule.completion > 0 && schedule.completion < 100;
      if (key === 'inactive') return schedule.completion === 0;
      return true;
    });
    const label = metricLabels[key];

    setSelectedEventRange(null);
    setSelectedUserRange(null);
    setSelectedScheduleRange({
      key: `metric-${key}`,
      label,
      drawerTitle: label,
      value: schedules.length,
      schedules,
    });
  };
  const userMetricLabels = Object.fromEntries(USER_METRIC_CARD_KEYS.map(key => [
    key,
    intl.formatMessage({ id: `${userMessages}.metric.${key}` }),
  ])) as Record<(typeof USER_METRIC_CARD_KEYS)[number], string>;
  const openUserMetric = (key: 'assigned' | 'active' | 'complete' | 'partial' | 'inactive') => {
    const allUsers = data.completionRanges.flatMap(range => range.users);
    const users = allUsers.filter(user => {
      if (key === 'assigned') return true;
      if (user.status) {
        if (key === 'active') return user.completed > 0;
        return user.status === key;
      }
      if (key === 'active') return user.completion > 0;
      if (key === 'complete') return user.completion === 100;
      if (key === 'partial') return user.completion > 0 && user.completion < 100;
      if (key === 'inactive') return user.completion === 0;
      return true;
    });
    const label = userMetricLabels[key];

    setSelectedScheduleRange(null);
    setSelectedEventRange(null);
    setSelectedUserRange({
      key: `metric-${key}`,
      label,
      drawerTitle: label,
      value: users.length,
      users,
    });
  };

  const metricCards = data.metrics.map(metric => {
    const isInteractive = ['schedules', 'completed', 'partial', 'inactive'].includes(metric.key);
    return {
      key: metric.key,
      value: metric.value,
      label: metricLabels[metric.key],
      detail: intl.formatMessage(
        { id: `dashboards.schedules.summary.metric.${metric.key}.detail` },
        metric.detailValues,
      ),
      ...(isInteractive ? {
        onClick: () => openScheduleMetric(metric.key as 'schedules' | 'completed' | 'partial' | 'inactive'),
        actionLabel: `${metricLabels[metric.key]}: ${metric.value}`,
      } : {}),
    };
  });
  const userMetricCards = USER_METRIC_CARD_KEYS.map(key => {
    const value = key === 'average'
      ? intl.formatNumber(apiUserMetricValues.average, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
      : apiUserMetricValues[key];
    const label = userMetricLabels[key];
    const isInteractive = key !== 'average';
    return {
      key: `user-${key}`,
      value,
      label,
      detail: intl.formatMessage({ id: `${userMessages}.metric.${key}.detail` }),
      ...(isInteractive ? {
        onClick: () => openUserMetric(key as 'assigned' | 'active' | 'complete' | 'partial' | 'inactive'),
        actionLabel: `${label}: ${value}`,
      } : {}),
    };
  });

  return (
    <div className={styles.summary} data-testid={variant === 'routes' ? 'route-summary' : 'schedule-summary'}>
      <ReportGrid>
      <MetricsPanel
        ariaLabel={intl.formatMessage({ id: 'dashboards.schedules.summary.metrics' })}
        groups={[
          {
            label: intl.formatMessage({ id: 'dashboards.schedules.summary.metricGroup.schedules' }),
            items: metricCards,
            status: statusFor('schedule-overview'),
          },
          {
            label: intl.formatMessage({ id: 'dashboards.schedules.summary.metricGroup.users' }),
            items: userMetricCards,
            status: statusFor('schedule-users'),
          },
        ]}
      />

      <SummarySection
        className={styles.complianceEvolutionSection}
        title={intl.formatMessage({ id: 'dashboards.schedules.summary.complianceEvolution' })}
        description={intl.formatMessage({
          id: 'dashboards.schedules.summary.complianceEvolution.description',
        })}
        headerExtra={(
          <ChartControls
            view={{
              value: complianceChartView,
              labels: chartViewLabels,
              onChange: handleComplianceChartViewChange,
            }}
            pager={{
              page: currentCompliancePage,
              pageSize: chartPageSize,
              pageCount: compliancePageCount,
              total: data.period.length,
              ariaLabel: intl.formatMessage({
                id: 'dashboards.schedules.summary.complianceEvolution.navigation',
              }),
              onChange: setCompliancePage,
            }}
          />
        )}
        status={statusFor('schedule-overview')}
      >
          <div
            ref={complianceChartRef}
            role="img"
            aria-label={intl.formatMessage({
              id: 'dashboards.schedules.summary.complianceEvolution',
            })}
          >
            <PercentTrendChart
              data={complianceData}
              view={complianceChartView}
              tooltipLabel={intl.formatMessage({
                id: 'dashboards.schedules.summary.complianceEvolution.tooltipLabel',
              })}
              formatTooltipDetail={({ completed, total }) => intl.formatMessage(
                { id: 'dashboards.schedules.summary.complianceEvolution.tooltipDetail' },
                { completed, total },
              )}
            />
          </div>
      </SummarySection>

      <div className={styles.twoColumnGrid}>
        <SummarySection
          className={styles.periodResultSection}
          title={intl.formatMessage({ id: 'dashboards.schedules.summary.periodResult' })}
          description={intl.formatMessage({ id: 'dashboards.schedules.summary.periodResult.description' })}
          status={statusFor('schedule-overview')}
        >
          <div className={styles.resultLayout}>
            <div
              className={styles.donutChart}
              role="img"
              aria-label={intl.formatMessage({ id: 'dashboards.schedules.summary.periodResult' })}
            >
              <Pie
                data={resultChartData}
                angleField="value"
                colorField="status"
                radius={0.92}
                innerRadius={0.7}
                height={180}
                color={SCHEDULE_RESULT_COLORS}
                legend={false}
                animation={false}
                statistic={{
                  title: false,
                  content: { content: String(data.totalSchedules) },
                }}
              />
            </div>
            <div className={styles.legend}>
              {resultChartData.map((result, index) => (
                <div key={result.key} className={styles.legendRow}>
                  <span
                    className={styles.legendDot}
                    style={{ backgroundColor: SCHEDULE_RESULT_COLORS[index] }}
                    aria-hidden="true"
                  />
                  <Text className={styles.legendLabel}>{result.status}</Text>
                  <Text>{result.value}</Text>
                  <Text type="secondary">{result.percentage}%</Text>
                  <Text type="secondary" className={styles.legendDescription}>
                    {intl.formatMessage({
                      id: `dashboards.schedules.summary.result.${result.key}.description`,
                    })}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </SummarySection>

        <SummarySection
          title={intl.formatMessage({ id: 'dashboards.schedules.summary.scheduleCompliance' })}
          description={intl.formatMessage({
            id: 'dashboards.schedules.summary.scheduleCompliance.description',
          })}
          status={statusFor('schedule-compliance')}
        >
          <ProgressList
            className={styles.scheduleComplianceList}
            items={data.scheduleCompletionRanges.map(range => ({
              ...range,
              barColor: getComplianceRangeColor(range.key),
              actionLabel: intl.formatMessage(
                { id: 'dashboards.schedules.summary.scheduleCompliance.openRange' },
                { count: range.value, range: range.label },
              ),
            }))}
            onValueClick={item => {
              const range = data.scheduleCompletionRanges.find(({ key }) => key === item.key);
              if (range) {
                setSelectedScheduleRange(range);
              }
            }}
          />
        </SummarySection>
      </div>

      </ReportGrid>

      <DrilldownDrawer
        open={selectedScheduleRange !== null}
        title={selectedScheduleRange
          ? selectedScheduleRange.drawerTitle ?? intl.formatMessage(
              { id: 'dashboards.schedules.summary.scheduleCompliance.drawerTitle' },
              { range: selectedScheduleRange.label },
            )
          : ''}
        summary={intl.formatMessage(
          { id: 'dashboards.schedules.summary.scheduleCompliance.drawerSummary' },
          { count: drawerTotal },
        )}
        tag={selectedScheduleRange?.label}
        search={{
          value: scheduleSearch,
          placeholder: intl.formatMessage({
            id: 'dashboards.schedules.summary.scheduleCompliance.search',
          }),
          onChange: value => {
            setScheduleSearch(value);
            setDrilldownPage(1);
          },
        }}
        onClose={() => setSelectedScheduleRange(null)}
        pagination={drawerPagination}
      >
        <SectionStatusContent status={drilldownStatus}>
          <ResponsiveTable<ScheduleSummarySchedule>
            className={styles.scheduleDrawerTable}
            columns={scheduleDrawerColumns}
            dataSource={drawerSchedules}
            dataResetKey={`${selectedScheduleRange?.key}-${scheduleSearch}-${drilldownPage}`}
            loading={scheduleDrilldown.loading}
            stickyHeader
            maxHeight={DRILLDOWN_TABLE_MAX_HEIGHT}
            type="light"
            wrapperClassName={styles.scheduleDrawerTableWrapper}
          />
        </SectionStatusContent>
      </DrilldownDrawer>

      <SummarySection
        title={intl.formatMessage({ id: variant === 'routes' ? 'dashboards.routes.summary.executions' : 'dashboards.schedules.summary.executions' })}
        description={intl.formatMessage({
          id: variant === 'routes' ? 'dashboards.routes.summary.executions.description' : 'dashboards.schedules.summary.executions.description',
        })}
        status={statusFor('schedule-overview')}
        headerExtra={executionPageCount > 1 ? (
          <Pagination
            aria-label={intl.formatMessage({
              id: variant === 'routes' ? 'dashboards.routes.summary.executions.navigation' : 'dashboards.schedules.summary.executions.navigation',
            })}
            className={styles.chartPagination}
            current={currentExecutionPage + 1}
            pageSize={chartPageSize}
            simple
            showSizeChanger={false}
            total={data.period.length}
            onChange={page => setExecutionPage(page - 1)}
          />
        ) : undefined}
        context={(
          <div className={styles.summaryContext}>
            <Text strong>
              {intl.formatMessage(
                { id: variant === 'routes' ? 'dashboards.routes.summary.executions.completedSummary' : 'dashboards.schedules.summary.executions.completedSummary' },
                { completed: executionCounts.completed, total: executionCounts.expected },
              )}
            </Text>
            <Text type="secondary">
              {intl.formatMessage(
                { id: variant === 'routes' ? 'dashboards.routes.summary.executions.rateSummary' : 'dashboards.schedules.summary.executions.rateSummary' },
                { rate: executionCompletion },
              )}
            </Text>
          </div>
        )}
      >
        <div
          ref={executionChartRef}
          role="img"
          aria-label={intl.formatMessage({ id: variant === 'routes' ? 'dashboards.routes.summary.executions' : 'dashboards.schedules.summary.executions' })}
        >
          <Column
            data={executionsData}
            xField="period"
            yField="value"
            seriesField="type"
            isGroup
            height={230}
            color={SCHEDULE_EXECUTION_COLORS}
            columnWidthRatio={0.72}
            maxColumnWidth={72}
            columnStyle={{ radius: [2, 2, 0, 0] }}
            xAxis={{ label: { autoHide: true, autoRotate: false } }}
            legend={{ position: 'top-right' }}
            animation={false}
          />
        </div>
      </SummarySection>

      <div className={styles.twoColumnGrid}>
        <SummarySection
          className={styles.eventComplianceSection}
          title={intl.formatMessage({ id: 'dashboards.schedules.summary.eventCompliance' })}
          description={intl.formatMessage({
            id: 'dashboards.schedules.summary.eventCompliance.description',
          })}
          status={statusFor('schedule-events')}
        >
          <ProgressList
            className={styles.eventComplianceList}
            items={data.eventComplianceRanges.map(range => {
              const label = intl.formatMessage({
                id: `dashboards.schedules.summary.eventCompliance.range.${range.label}`,
              });

              return {
                ...range,
                label,
                barColor: getComplianceRangeColor(range.key),
                actionLabel: intl.formatMessage(
                  { id: 'dashboards.schedules.summary.eventCompliance.openRange' },
                  { count: range.value, range: label },
                ),
              };
            })}
            onValueClick={item => {
              const range = data.eventComplianceRanges.find(({ key }) => key === item.key);
              if (range) {
                setSelectedScheduleRange(null);
                setSelectedEventRange(range);
              }
            }}
            separated
          />
        </SummarySection>
        <SummarySection
          className={styles.eventTypesSection}
          title={intl.formatMessage({ id: 'dashboards.schedules.summary.eventTypes' })}
          description={intl.formatMessage({ id: 'dashboards.schedules.summary.eventTypes.description' })}
          status={statusFor('schedule-events')}
        >
          <ProgressList
            className={styles.rankedEventTypesList}
            items={data.eventTypes.slice(0, 5)}
            separated
            showRank
          />
        </SummarySection>
      </div>

      <DrilldownDrawer
        open={selectedEventRange !== null}
        title={selectedEventRange
          ? intl.formatMessage(
              { id: 'dashboards.schedules.summary.eventCompliance.drawerTitle' },
              {
                range: intl.formatMessage({
                  id: `dashboards.schedules.summary.eventCompliance.range.${selectedEventRange.label}`,
                }),
              },
            )
          : ''}
        summary={intl.formatMessage(
          { id: 'dashboards.schedules.summary.eventCompliance.drawerSummary' },
          { count: drawerTotal },
        )}
        tag={selectedEventRange
          ? intl.formatMessage({
              id: `dashboards.schedules.summary.eventCompliance.range.${selectedEventRange.label}`,
            })
          : undefined}
        search={{
          value: eventSearch,
          placeholder: intl.formatMessage({
            id: 'dashboards.schedules.summary.eventCompliance.search',
          }),
          onChange: value => {
            setEventSearch(value);
            setDrilldownPage(1);
          },
        }}
        onClose={() => setSelectedEventRange(null)}
        pagination={drawerPagination}
      >
        <SectionStatusContent status={drilldownStatus}>
          <ResponsiveTable
            className={styles.scheduleDrawerTable}
            columns={eventDrawerColumns}
            dataSource={drawerEventTypes}
            dataResetKey={`${selectedEventRange?.key}-${eventSearch}-${drilldownPage}`}
            loading={scheduleDrilldown.loading}
            stickyHeader
            maxHeight={DRILLDOWN_TABLE_MAX_HEIGHT}
            type="light"
            wrapperClassName={styles.scheduleDrawerTableWrapper}
          />
        </SectionStatusContent>
      </DrilldownDrawer>

      {showDivisionCompliance && (
        <div className={styles.divisionAnalyticsGrid}>
          <SummarySection
            title={intl.formatMessage({ id: 'dashboards.schedules.summary.divisionAllocation' })}
            description={intl.formatMessage({
              id: 'dashboards.schedules.summary.divisionAllocation.description',
            })}
            status={statusFor('schedule-divisions')}
            headerExtra={allocationPageCount > 1 ? (
              <Pagination
                aria-label={intl.formatMessage({
                  id: 'dashboards.schedules.summary.divisionAllocation.navigation',
                })}
                className={styles.chartPagination}
                current={currentAllocationPage + 1}
                pageSize={DIVISION_PAGE_SIZE}
                simple
                showSizeChanger={false}
                total={data.divisionScheduleAssignments.length}
                onChange={page => setDivisionAllocationPage(page - 1)}
              />
            ) : undefined}
          >
            <div className={styles.divisionAllocationLayout}>
              <div
                className={styles.divisionDonutChart}
                role="img"
                aria-label={intl.formatMessage({ id: 'dashboards.schedules.summary.divisionAllocation' })}
              >
                <Pie
                  data={allocationPageItems.map((item, index) => ({
                    division: item.label,
                    value: item.value,
                    color: SCHEDULE_DIVISION_COLORS[
                      (currentAllocationPage * DIVISION_PAGE_SIZE + index)
                      % SCHEDULE_DIVISION_COLORS.length
                    ],
                  }))}
                  angleField="value"
                  colorField="division"
                  radius={0.92}
                  innerRadius={0.68}
                  height={248}
                  color={SCHEDULE_DIVISION_COLORS}
                  label={false}
                  legend={false}
                  animation={false}
                  statistic={{
                    title: {
                      content: intl.formatMessage({ id: 'dashboards.schedules.summary.divisionAllocation.total' }),
                    },
                    content: { content: String(allocationPageTotal) },
                  }}
                />
              </div>
              <div className={styles.divisionAllocationLegend}>
                {allocationPageItems.map((item, index) => (
                  <div className={styles.divisionAllocationRow} key={item.key}>
                    <span
                      className={styles.progressColorIndicator}
                      style={{
                        backgroundColor: SCHEDULE_DIVISION_COLORS[
                          (currentAllocationPage * DIVISION_PAGE_SIZE + index)
                          % SCHEDULE_DIVISION_COLORS.length
                        ],
                      }}
                      aria-hidden="true"
                    />
                    <Text className={styles.divisionAllocationLabel} ellipsis={{ tooltip: item.label }}>
                      {item.label}
                    </Text>
                    <Text strong>{item.value}</Text>
                  </div>
                ))}
              </div>
            </div>
          </SummarySection>

          <SummarySection
            title={intl.formatMessage({ id: 'dashboards.schedules.summary.divisionCompliance' })}
            description={intl.formatMessage({
              id: 'dashboards.schedules.summary.divisionCompliance.description',
            })}
            status={statusFor('schedule-divisions')}
            headerExtra={divisionCompliancePageCount > 1 ? (
              <Pagination
                aria-label={intl.formatMessage({
                  id: 'dashboards.schedules.summary.divisionCompliance.navigation',
                })}
                className={styles.chartPagination}
                current={currentDivisionCompliancePage + 1}
                pageSize={DIVISION_PAGE_SIZE}
                simple
                showSizeChanger={false}
                total={divisionCompletionItems.length}
                onChange={page => setDivisionCompliancePage(page - 1)}
              />
            ) : undefined}
          >
            <ProgressList
              className={styles.divisionCompletionList}
              items={divisionCompliancePageItems}
              separated
              testId="division-compliance-list"
            />
          </SummarySection>
        </div>
      )}

      <div className={styles.twoColumnGrid}>
        <SummarySection
          className={styles.userCompletionSection}
          title={intl.formatMessage({ id: 'dashboards.schedules.summary.completionRanges' })}
          description={intl.formatMessage({ id: 'dashboards.schedules.summary.completionRanges.description' })}
          status={statusFor('schedule-users')}
          context={(
            <div className={styles.compactContext}>
              <Text>
                {intl.formatMessage(
                  { id: 'dashboards.schedules.summary.users.assigned' },
                  { count: data.userSummary.assigned },
                )}
              </Text>
              <Text>
                {intl.formatMessage(
                  { id: 'dashboards.schedules.summary.users.active' },
                  { count: data.userSummary.active },
                )}
              </Text>
              <Text>
                {intl.formatMessage(
                  { id: 'dashboards.schedules.summary.users.inactive' },
                  { count: data.userSummary.inactive },
                )}
              </Text>
            </div>
          )}
        >
          <ProgressList
            className={styles.userComplianceList}
            items={data.completionRanges.map(range => ({
              ...range,
              barColor: getComplianceRangeColor(range.key),
              actionLabel: intl.formatMessage(
                { id: 'dashboards.schedules.summary.users.openRange' },
                { count: range.value, range: range.label },
              ),
            }))}
            onValueClick={item => {
              const range = data.completionRanges.find(({ key }) => key === item.key);
              if (range) {
                setSelectedScheduleRange(null);
                setSelectedEventRange(null);
                setSelectedUserRange(range);
              }
            }}
          />
        </SummarySection>
      </div>

      <DrilldownDrawer
        open={selectedUserRange !== null}
        title={selectedUserRange
          ? selectedUserRange.drawerTitle ?? intl.formatMessage(
              { id: 'dashboards.schedules.summary.users.drawerTitle' },
              { range: selectedUserRange.label },
            )
          : ''}
        summary={intl.formatMessage(
          { id: 'dashboards.schedules.summary.users.drawerSummary' },
          { count: drawerTotal },
        )}
        tag={selectedUserRange?.label}
        search={{
          value: userSearch,
          placeholder: intl.formatMessage({ id: 'dashboards.schedules.summary.users.search' }),
          onChange: value => {
            setUserSearch(value);
            setDrilldownPage(1);
          },
        }}
        onClose={() => setSelectedUserRange(null)}
        pagination={drawerPagination}
      >
        <SectionStatusContent status={drilldownStatus}>
          <ResponsiveTable<ScheduleSummaryUser>
            className={styles.scheduleDrawerTable}
            columns={userDrawerColumns}
            dataSource={drawerUsers}
            dataResetKey={`${selectedUserRange?.key}-${userSearch}-${drilldownPage}`}
            loading={scheduleDrilldown.loading}
            stickyHeader
            maxHeight={DRILLDOWN_TABLE_MAX_HEIGHT}
            type="light"
            wrapperClassName={styles.scheduleDrawerTableWrapper}
          />
        </SectionStatusContent>
      </DrilldownDrawer>

    </div>
  );
};

export default React.memo(ScheduleSummary);
